import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart, faHeartRegular, faPen, faSpinner } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { handleAPIError } from '@/utils/toasts';
import { useUser } from '@/contexts/UserContext';
import EditFavoriteModal from '@/components/trading/EditFavoriteModal';
import type { RecommendationFavorite } from '@/models';
import type { CreateFavoriteRequest } from '@/modelsAPI';
import type { TradingRecommendationType } from '@/modelsLocal/trading-recommendation';
import type {
  CoreRecommendationID,
  EarningsRecommendationID,
  SwingTradeID,
  LongTermRecommendationID,
} from '@/models/ids';

interface FavoriteControlProps {
  type: TradingRecommendationType;
  recommendationId: string;
  favorite: RecommendationFavorite | null;
  onChange: (favorite: RecommendationFavorite | null) => void;
  className?: string;
}

export default function FavoriteControl({ type, recommendationId, favorite, onChange, className = '' }: FavoriteControlProps) {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
  const [showEdit, setShowEdit] = useState(false);

  // Favorites require an authenticated user
  if (user === null) return null;

  const isFavorited = favorite !== null;
  // Highlight favorites that the user has marked as an active trade.
  const isActive = favorite !== null && favorite.active === true;

  const stop = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const buildCreateRequest = (): CreateFavoriteRequest => {
    if (type === 'CORE') return { coreRecommendationID: recommendationId as CoreRecommendationID };
    if (type === 'EARNINGS') return { earningsRecommendationID: recommendationId as EarningsRecommendationID };
    if (type === 'SWING') return { swingTradeRecommendationID: recommendationId as SwingTradeID };
    return { longTermRecommendationID: recommendationId as LongTermRecommendationID };
  };

  const handleToggle = async (e: React.MouseEvent) => {
    stop(e);
    if (loading) return;
    setLoading(true);
    try {
      if (favorite !== null) {
        await APIRoutes.deleteFavorite(favorite.id);
        onChange(null);
      } else {
        const created = await APIRoutes.createFavorite(buildCreateRequest());
        onChange(created);
      }
    } catch (error) {
      handleAPIError(error, 'Der Favorit konnte nicht gespeichert werden. Bitte versuche es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    stop(e);
    setShowEdit(true);
  };

  return (
    <>
      <div className={`flex items-center gap-1.5 ${className}`}>
        {isFavorited && (
          <button
            type="button"
            onClick={handleEdit}
            aria-label="Favorit bearbeiten"
            title="Favorit bearbeiten"
            className="flex items-center justify-center w-8 h-8 rounded-full bg-white/90 dark:bg-gray-800/90 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 shadow-sm cursor-pointer transition-colors"
          >
            <FontAwesomeIcon icon={faPen} className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={handleToggle}
          disabled={loading}
          aria-label={isFavorited ? 'Favorit entfernen' : 'Zu Favoriten hinzufügen'}
          title={isFavorited ? 'Favorit entfernen' : 'Zu Favoriten hinzufügen'}
          className={`flex items-center justify-center w-8 h-8 rounded-full bg-white/90 dark:bg-gray-800/90 shadow-sm cursor-pointer transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
            isFavorited
              ? 'text-red-500 hover:text-red-600'
              : 'text-gray-400 hover:text-red-500'
          } ${isActive ? 'animate-favorite-active' : ''}`}
        >
          {loading ? (
            <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
          ) : (
            <FontAwesomeIcon icon={isFavorited ? faHeart : faHeartRegular} className="w-4 h-4" />
          )}
        </button>
      </div>

      {favorite !== null && (
        <EditFavoriteModal
          isOpen={showEdit}
          onClose={() => setShowEdit(false)}
          favorite={favorite}
          onFavoriteUpdated={(updated) => onChange(updated)}
        />
      )}
    </>
  );
}
